/**
 * KqshRetentionPageV2 — tra cứu bảo lưu hồ sơ sát hạch.
 *
 * Trả lời một câu hỏi mà hôm nay không có chỗ nào trả lời được: hồ sơ học viên
 * này còn hạn thi lại đến bao giờ. Người hỏi là chính người đang ngồi ở màn
 * đăng ký thi lại A1/A (`/a1a`), nên trang này đặt cạnh nó chứ không nằm bên
 * uniapp-frontend.
 *
 * CHỈ ĐỌC. Nhập file kết quả sát hạch nằm ở uniapp-frontend
 * (`/admin/getfly/kqsh`), nơi sau này có nút đồng bộ sang GetFly. Backend cũng
 * chặn ghi với vai trò giáo viên.
 *
 * Mốc bảo lưu tính từ lần hồ sơ "đứng lại" gần nhất — rớt hoặc vắng — cộng 12
 * tháng. Hai nguồn mốc đó tách cột riêng vì khi đối chiếu hồ sơ giấy thì rớt
 * và vắng là hai chuyện khác nhau.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Badge, Button, Form, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import kqshService from '../../services/kqsh';
import V2PageLayout from '../../components/v2/V2PageLayout';

const PAGE_LIMIT = 50;

const NHOM_LABEL = { A1: 'A1', A: 'A (cũ A2)', B_C1: 'B / C1' };
const MON_LABEL = {
  LY_THUYET: 'Lý thuyết', MO_PHONG: 'Mô phỏng', HINH: 'Sa hình', DUONG: 'Đường trường',
};
const MOC_LABEL = { ROT: 'Rớt', VANG: 'Vắng', ROT_VANG_CUNG_NGAY: 'Rớt + Vắng (cùng ngày)' };

const fmt = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};
const homNay = () => new Date().toISOString().slice(0, 10);
/** Cộng tháng, kẹp về ngày cuối tháng — cùng phép tính với backend. */
const congThang = (iso, n) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!m) return '';
  const tong = +m[1] * 12 + (+m[2] - 1) + n;
  const y = Math.floor(tong / 12);
  const mo = (tong % 12) + 1;
  const cuoi = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  const d = Math.min(+m[3], cuoi);
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

export default function KqshRetentionPageV2() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [nhomHang, setNhomHang] = useState('');
  const [trangThai, setTrangThai] = useState('');
  const [sapHetHan, setSapHetHan] = useState(false);

  const load = useCallback(async (p = 1, tuKhoa = q) => {
    setLoading(true);
    try {
      const params = { page: p, limit: PAGE_LIMIT };
      if (tuKhoa) params.q = tuKhoa;
      if (nhomHang) params.nhomHang = nhomHang;
      if (trangThai === 'dat') params.daDat = true;
      if (trangThai === 'chuaDat') params.daDat = false;
      if (trangThai === 'canXemLai') params.canXemLai = true;
      if (trangThai === 'chiVang') params.chiVang = true;
      // "Sắp hết hạn" = hạn rơi vào trong 3 tháng tới.
      if (sapHetHan) params.hetHanTruoc = congThang(homNay(), 3);
      const res = await kqshService.getRetention(params);
      setRows(res?.data?.docs || []);
      setTotal(res?.data?.totalDocs || 0);
      setPage(p);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [q, nhomHang, trangThai, sapHetHan]);

  useEffect(() => { load(1); }, [nhomHang, trangThai, sapHetHan]); // eslint-disable-line react-hooks/exhaustive-deps

  const columnDefs = useMemo(() => [
    { field: 'hoTen', headerName: 'Họ và tên', minWidth: 200, pinned: 'left' },
    {
      field: 'cccd',
      headerName: 'CCCD',
      minWidth: 150,
      valueGetter: (p) => p.data.cccd || p.data.soGiayTo,
      cellRenderer: (p) => (
        <span>
          {p.data.cccd || p.data.soGiayTo}
          {p.data.dinhDanhKhongChuan && (
            <Badge bg='warning' text='dark' className='ms-1'>không phải CCCD</Badge>
          )}
        </span>
      ),
    },
    { field: 'hang', headerName: 'Hạng', width: 95 },
    {
      field: 'nhomHang',
      headerName: 'Nhóm',
      width: 115,
      valueGetter: (p) => NHOM_LABEL[p.data.nhomHang] || '(không rõ)',
    },
    {
      headerName: 'HẠN BẢO LƯU',
      field: 'hanBaoLuu',
      width: 165,
      pinned: 'left',
      cellRenderer: (p) => {
        const d = p.data;
        if (d.daDatSatHach) {
          return (
            <Badge bg={d.canXemLai ? 'warning' : 'success'} text={d.canXemLai ? 'dark' : undefined}>
              {d.canXemLai ? 'Đạt — xem lại' : 'Đã đạt'}
            </Badge>
          );
        }
        if (!d.hanBaoLuu) return <span className='text-muted'>chưa tính được</span>;
        const het = d.hanBaoLuu < homNay();
        const sap = !het && d.hanBaoLuu <= congThang(homNay(), 3);
        return (
          <span className={het ? 'text-danger fw-bold' : (sap ? 'text-warning fw-semibold' : '')}>
            {fmt(d.hanBaoLuu)}{het ? ' — hết hạn' : ''}
          </span>
        );
      },
    },
    {
      field: 'mocBaoLuu',
      headerName: 'Mốc',
      width: 120,
      valueGetter: (p) => (p.data.mocBaoLuu ? fmt(p.data.mocBaoLuu) : '-'),
    },
    {
      field: 'mocTinhTheo',
      headerName: 'Tính theo',
      width: 155,
      cellRenderer: (p) => {
        if (!p.data.mocTinhTheo) return '-';
        return (
          <span>
            {MOC_LABEL[p.data.mocTinhTheo] || p.data.mocTinhTheo}
            {p.data.chiVangChuaTungRot && <Badge bg='info' className='ms-1'>chưa từng rớt</Badge>}
          </span>
        );
      },
    },
    {
      headerName: 'Môn quyết định mốc',
      minWidth: 170,
      // Câu hỏi đầu tiên khi nhìn hạn bảo lưu là 'vì sao lại ngày này'. Mốc là
      // ngày muộn nhất trong CÁC MÔN BẮT BUỘC — chỉ rõ môn đã tạo ra nó.
      valueGetter: (p) => {
        const d = p.data;
        if (!d.mocBaoLuu) return '-';
        const req = d.monBatBuoc || [];
        return Object.keys(MON_LABEL)
          .filter((m) => req.includes(m))
          .filter((m) => d.mon?.[m]?.ngayRotGanNhat === d.mocBaoLuu
            || d.mon?.[m]?.ngayVangGanNhat === d.mocBaoLuu)
          .map((m) => MON_LABEL[m])
          .join(', ') || '-';
      },
    },
    {
      headerName: 'Rớt gần nhất theo môn',
      minWidth: 300,
      valueGetter: (p) => Object.keys(MON_LABEL)
        .map((m) => {
          const x = p.data.mon?.[m];
          return x?.ngayRotGanNhat ? `${MON_LABEL[m]} ${fmt(x.ngayRotGanNhat)}` : null;
        })
        .filter(Boolean)
        .join(' · ') || '-',
    },
    {
      headerName: 'Vắng gần nhất theo môn',
      minWidth: 300,
      valueGetter: (p) => Object.keys(MON_LABEL)
        .map((m) => {
          const x = p.data.mon?.[m];
          return x?.ngayVangGanNhat ? `${MON_LABEL[m]} ${fmt(x.ngayVangGanNhat)}` : null;
        })
        .filter(Boolean)
        .join(' · ') || '-',
    },
    {
      field: 'monThieuDuLieu',
      headerName: 'Thiếu dữ liệu',
      minWidth: 180,
      valueGetter: (p) => (p.data.monThieuDuLieu || []).map((m) => MON_LABEL[m] || m).join(', ') || '-',
    },
    { field: 'canCu', headerName: 'Căn cứ', minWidth: 320 },
    { field: 'phanQuyetGanNhat', headerName: 'Phán quyết kỳ gần nhất', minWidth: 280 },
    {
      field: 'kyDaDu',
      headerName: 'Kỳ đã dự',
      minWidth: 200,
      valueGetter: (p) => (p.data.kyDaDu || []).map(fmt).join(', '),
    },
    { field: 'nguonHang', headerName: 'Nguồn hạng', minWidth: 240 },
  ], []);

  const soTrang = Math.ceil(total / PAGE_LIMIT) || 1;

  return (
    <V2PageLayout
      title={<>Sát hạch &amp; bảo lưu hồ sơ <Badge bg='secondary' className='ms-2'>{total}</Badge></>}
      filters={(
        <div className='d-flex flex-wrap gap-2 align-items-center'>
          <InputGroup size='sm' style={{ width: 280 }}>
            <Form.Control
              placeholder='Tên hoặc CCCD'
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load(1)}
            />
            <Button variant='outline-secondary' onClick={() => load(1)}>Tìm</Button>
          </InputGroup>
          <Form.Select size='sm' style={{ width: 150 }} value={nhomHang} onChange={(e) => setNhomHang(e.target.value)}>
            <option value=''>Mọi hạng</option>
            <option value='A1'>A1</option>
            <option value='A'>A (cũ A2)</option>
            <option value='B_C1'>B / C1</option>
          </Form.Select>
          <Form.Select size='sm' style={{ width: 230 }} value={trangThai} onChange={(e) => setTrangThai(e.target.value)}>
            <option value=''>Mọi trạng thái</option>
            <option value='chuaDat'>Chưa đạt</option>
            <option value='dat'>Đã đạt sát hạch</option>
            <option value='canXemLai'>Cần xem lại (rớt vì Mô phỏng)</option>
            <option value='chiVang'>Chỉ vắng, chưa từng rớt</option>
          </Form.Select>
          <Form.Check
            type='switch'
            id='kqsh-sap-het-han'
            label='Sắp hết hạn (3 tháng)'
            checked={sapHetHan}
            onChange={(e) => setSapHetHan(e.target.checked)}
          />
        </div>
      )}
      footer={soTrang > 1 && (
        <div className='d-flex justify-content-center align-items-center gap-2'>
          <Button size='sm' variant='outline-secondary' disabled={page <= 1 || loading} onClick={() => load(page - 1)}>Trước</Button>
          <span className='small'>Trang {page} / {soTrang}</span>
          <Button size='sm' variant='outline-secondary' disabled={page >= soTrang || loading} onClick={() => load(page + 1)}>Sau</Button>
        </div>
      )}
    >
      <AgGridReact
        rowData={rows}
        columnDefs={columnDefs}
        loading={loading}
        pagination={false}
        defaultColDef={{ resizable: true, sortable: true, suppressHeaderMenuButton: true }}
        overlayNoRowsTemplate='<span class="ag-overlay-loading-center">Không có học viên nào khớp</span>'
      />
    </V2PageLayout>
  );
}
